import { z } from 'zod';
import { isoDateTimeSchema, uuidSchema } from './common.js';
/**
 * `/api/v1/auth/login` input (admin-arch §3.2).
 *
 * `totp_code` is optional; users with 2FA enabled must include it on the same
 * request (one-shot login) OR follow the staged `requires_2fa` flow. The DTO
 * accepts both shapes — the auth service decides which branch applies.
 *
 * `recovery_code` is the "I lost my phone" alternative to `totp_code`
 * (TASK-450). It has to live HERE rather than only on `/auth/2fa/recovery`,
 * because that endpoint needs an access token an enrolled user cannot obtain:
 * login answers `requires_2fa` and issues nothing. Same single-use semantics
 * as the authenticated endpoint. `totp_code` wins when both are supplied.
 */
export const loginInputSchema = z.object({
    email: z.string().email().max(255).toLowerCase(),
    password: z.string().min(8).max(256),
    totp_code: z
        .string()
        .regex(/^\d{6}$/, 'TOTP code must be 6 digits')
        .optional(),
    recovery_code: z
        .string()
        .min(8)
        .max(32)
        .regex(/^[A-Z0-9-]+$/, 'Recovery code must be uppercase alphanumeric')
        .optional(),
    device_fingerprint: z.string().min(1).max(255).optional(),
});
/**
 * `/auth/login` response is a discriminated union on `status`:
 *
 *   - `ok` — credentials accepted (and 2FA satisfied where enrolled);
 *     `access_token` is in the body, refresh token is an httpOnly cookie.
 *   - `requires_2fa` — the user has 2FA enabled, TOTP code missing.
 *   - `requires_2fa_setup` — **DEPRECATED, never emitted since TASK-450**
 *     (CEO 2026-07-27: 2FA is voluntary). Kept in the union so a client that
 *     is newer than the API it happens to be talking to still parses the old
 *     shape during a rolling deploy. Do not add code that produces it.
 *
 * Frontend MUST narrow on `status` before reading other fields.
 */
export const loginResponseSchema = z.discriminatedUnion('status', [
    z.object({
        status: z.literal('ok'),
        access_token: z.string().min(1),
        expires_at: isoDateTimeSchema,
        user: z.object({
            id: uuidSchema,
            email: z.string().email(),
            name: z.string().min(1),
            role: z.string().min(1),
            is_super_admin: z.boolean(),
        }),
    }),
    z.object({
        status: z.literal('requires_2fa'),
        message: z.string(),
    }),
    z.object({
        status: z.literal('requires_2fa_setup'),
        message: z.string(),
    }),
]);
/**
 * `/auth/login` error body when the (email, ip) pair is currently locked
 * out by the brute-force protection (Wave 2.27 BE-1, decisions §39).
 *
 * Returned with HTTP **423 Locked**. The frontend MUST display
 * `retry_after_seconds` to the user and disable the form until then.
 * `locked_until` is ISO-8601 (UTC) — clients compute remaining time off it
 * if their clock is more reliable than the server-provided countdown.
 */
export const accountLockedErrorSchema = z.object({
    code: z.literal('ACCOUNT_LOCKED'),
    message: z.string(),
    locked_until: isoDateTimeSchema,
    retry_after_seconds: z.number().int().nonnegative(),
});
/**
 * `/api/v1/auth/refresh` response — refresh cookie is HTTP-only and
 * not in the body.
 */
export const refreshSchema = z.object({
    access_token: z.string().min(1),
    expires_at: isoDateTimeSchema,
});
export const userRoleEnum = z.enum([
    'admin',
    'selector',
    'client_owner',
    'client_manager',
]);
/**
 * Authenticated session payload returned by `/auth/me`. Used by the admin
 * SPA to drive routing and RBAC.
 */
export const sessionSchema = z.object({
    user_id: uuidSchema,
    email: z.string().email(),
    role: userRoleEnum,
    display_name: z.string().nullable(),
    '2fa_enabled': z.boolean(),
    issued_at: isoDateTimeSchema,
    expires_at: isoDateTimeSchema,
});
// --------------------------------------------------------------------------
// 2FA enrollment / verification / recovery (TASK-018, extended by TASK-450)
// --------------------------------------------------------------------------
/**
 * Response of `GET /auth/2fa/status` (TASK-450) — drives the enrolment card in
 * the admin SPA (`/settings/security`).
 *
 * Deliberately carries NO secret material: neither the TOTP secret nor the
 * recovery-code hashes, only how many unused codes are left.
 */
export const twoFaStatusResponseSchema = z.object({
    enabled: z.boolean(),
    enrolled_at: isoDateTimeSchema.nullable(),
    last_used_at: isoDateTimeSchema.nullable(),
    recovery_codes_remaining: z.number().int().nonnegative(),
});
/**
 * Response of `POST /auth/2fa/setup`. `secret_base32` and `recovery_codes`
 * are returned exactly once — the client MUST persist them client-side
 * (admin downloads PDF) because the server stores only encrypted secret
 * and bcrypt-hashed recovery codes.
 */
export const twoFaSetupResponseSchema = z.object({
    secret_base32: z.string().min(16),
    qr_code_uri: z.string().min(1),
    recovery_codes: z.array(z.string().min(8)).length(10),
});
/** Body of `POST /auth/2fa/verify` — confirms enrolment with a live code. */
export const twoFaVerifyInputSchema = z.object({
    totp_code: z.string().regex(/^\d{6}$/, 'TOTP code must be 6 digits'),
});
const recoveryCodeSchema = z
    .string()
    .min(8)
    .max(32)
    .regex(/^[A-Z0-9-]+$/, 'Recovery code must be uppercase alphanumeric');
export const twoFaRecoveryInputSchema = z.object({
    recovery_code: recoveryCodeSchema,
});
/**
 * Body of `POST /auth/2fa/disable`.
 *
 * Turning the second factor off requires proving possession of the factor
 * being removed, so a stolen access token alone cannot strip a victim's 2FA.
 * EITHER proof is accepted:
 *
 *   - `totp_code`     — the normal path, a live code from the authenticator;
 *   - `recovery_code` — the phone-lost path. Without it a user who got in with
 *     a recovery code could never turn 2FA off (disable demanded a live code,
 *     re-`setup` answers 409 while an enrolment exists), so their ten codes
 *     bought ten logins and then the account needed a super-admin reset.
 *
 * Exactly one must be present: accepting both would leave "which one gets
 * spent" ambiguous, and neither is a 400 rather than a silent no-op.
 */
export const twoFaDisableInputSchema = z
    .object({
    totp_code: z
        .string()
        .regex(/^\d{6}$/, 'TOTP code must be 6 digits')
        .optional(),
    recovery_code: recoveryCodeSchema.optional(),
})
    .strict()
    .refine((v) => (v.totp_code === undefined) !== (v.recovery_code === undefined), { message: 'Supply exactly one of totp_code or recovery_code' });
/**
 * Token bundle returned by /auth/2fa/verify, /auth/2fa/recovery and
 * /auth/refresh. Refresh token is set as httpOnly cookie; access token in
 * body lets the SPA inject it into Authorization headers.
 */
export const tokenBundleSchema = z.object({
    access_token: z.string().min(1),
    expires_at: isoDateTimeSchema,
});
// --------------------------------------------------------------------------
// Refresh / logout (TASK-019)
// --------------------------------------------------------------------------
export const refreshResponseSchema = tokenBundleSchema;
export const logoutResponseSchema = z.object({
    status: z.literal('ok'),
});
// --------------------------------------------------------------------------
// Password reset (TASK-020, Wave 2.30 BE-2)
//
// Two endpoints, both PUBLIC (no auth):
//   POST /auth/password-reset/request — email-only, always 204 (anti-enum).
//   POST /auth/password-reset/confirm — token + new_password, 204 on success,
//     410 Gone on `INVALID_TOKEN` (consumed / expired / unknown).
//
// Token format: 64 lowercase hex chars (`crypto.randomBytes(32)`). The DB
// stores only `sha256(token)` so a DB compromise does not yield live tokens.
// TTL is 2h, single-use; redeeming the token also revokes ALL existing
// user_sessions for the affected user (force-logout on password change).
// --------------------------------------------------------------------------
/**
 * `POST /auth/password-reset/request` body. `email` is trimmed +
 * lowercased before lookup at the service layer. Always returns 204
 * regardless of whether the user exists — required to prevent account
 * enumeration via the response shape or timing.
 */
export const passwordResetRequestSchema = z
    .object({
    email: z.string().trim().email().max(255),
})
    .strict();
/**
 * `POST /auth/password-reset/confirm` body.
 *
 * Password policy mirrors `acceptInviteRequestSchema` (min 12, max 255).
 * No further complexity rule at the schema level — the existing codebase
 * does not standardize one (`loginInputSchema.min(8)` is intentionally
 * weaker for legacy logins). When a project-wide policy lands it should
 * replace BOTH this and accept-invite in lock-step.
 */
export const passwordResetConfirmSchema = z
    .object({
    token: z
        .string()
        .length(64, 'Token must be 64 hex chars')
        .regex(/^[0-9a-f]+$/, 'Token must be lowercase hex'),
    new_password: z.string().min(12).max(255),
})
    .strict();
/**
 * Error body returned by `/auth/password-reset/confirm` with HTTP 410 Gone
 * when the token is unknown, expired, or already consumed. The three cases
 * are intentionally indistinguishable so a probing client cannot enumerate
 * live tokens.
 */
export const passwordResetInvalidTokenErrorSchema = z.object({
    code: z.literal('INVALID_TOKEN'),
    message: z.string(),
});
/**
 * Error body returned by `/auth/password-reset/confirm` with HTTP 400 when
 * the new password fails the policy gate. Mirrored as an error-code constant
 * so frontend can branch on it without parsing the message.
 */
export const PASSWORD_RESET_ERROR_CODES = {
    INVALID_TOKEN: 'INVALID_TOKEN',
    WEAK_PASSWORD: 'WEAK_PASSWORD',
};
//# sourceMappingURL=auth.js.map